/**
 * VIDEO ENGINE v1.0.0 — Module partagé écosystème Chanbé IA
 * ContenuIA / ProspIA / Agent Tout Terrain / StoryVerse / LookIA
 * Approche : Canvas 2D + AudioBufferSource + MediaRecorder
 */

/** Erreur de rendu vidéo, avec message lisible pour l'utilisateur. */
export class VideoEngineError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'VideoEngineError';
  }
}

/** Vérifie que le navigateur supporte le rendu vidéo. */
export function checkBrowserSupport(): { supported: boolean; reason: string } {
  if (typeof window === 'undefined') return { supported: false, reason: 'Environnement non supporté.' };
  if (typeof MediaRecorder === 'undefined') {
    return { supported: false, reason: "Votre navigateur ne supporte pas l'enregistrement vidéo. Utilisez Chrome ou Edge." };
  }
  if (typeof AudioContext === 'undefined' && typeof (window as any).webkitAudioContext === 'undefined') {
    return { supported: false, reason: "Votre navigateur ne supporte pas l'audio. Utilisez Chrome ou Edge." };
  }
  const canvas = document.createElement('canvas');
  if (typeof canvas.captureStream !== 'function') {
    return { supported: false, reason: "Votre navigateur ne supporte pas la capture vidéo. Utilisez Chrome ou Edge." };
  }
  return { supported: true, reason: '' };
}

export interface Subtitle { start: number; end: number; text: string; }
export interface VideoBranding { nom: string; couleur: string; afficherUrl?: string; position?: 'top-left' | 'top-right' | 'none'; }
export interface VideoInput { backgroundVideoUrl: string; audioB64: string; scriptForSubtitles: string; subtitles?: Subtitle[]; branding?: VideoBranding; format?: 'portrait' | 'landscape' | 'square'; onProgress?: (step: string, pct: number) => void; }
export interface VideoOutput { blob: Blob; url: string; mimeType: string; durationSec: number; sizeBytes: number; }

const DIMS: Record<string, { w: number; h: number }> = { portrait: { w: 720, h: 1280 }, landscape: { w: 1280, h: 720 }, square: { w: 1080, h: 1080 } };

export function generateSubtitles(script: string, duration: number): Subtitle[] {
  const cleaned = script.replace(/\[.*?\]/g, '').replace(/HOOK.*?:|PROBLEME.*?:|SOLUTION.*?:|BENEFICE.*?:|CTA.*?:/gi, '').replace(/\n+/g, ' ').trim();
  const sentences = cleaned.split(/[.!?]+/).map(s => s.trim()).filter(s => s.length > 3).slice(0, 10);
  if (!sentences.length) return [];
  const tps = duration / sentences.length;
  return sentences.map((text, i) => ({ start: i * tps, end: Math.min((i + 1) * tps - 0.15, duration), text: text.substring(0, 55) }));
}

function drawFrame(ctx: CanvasRenderingContext2D, video: HTMLVideoElement, t: number, subs: Subtitle[], W: number, H: number, branding?: VideoBranding, totalDuration?: number) {
  const duration = totalDuration || 30;
  const introEnd = 1.8;
  const outroStart = duration - 3;

  // INTRO BRANDING
  if (t < introEnd && branding) {
    const p = t / introEnd;
    const bg = ctx.createLinearGradient(0, 0, 0, H);
    bg.addColorStop(0, branding.couleur || '#6C47FF'); bg.addColorStop(1, '#0A0A1F');
    ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);
    ctx.strokeStyle = 'rgba(255,255,255,0.08)'; ctx.lineWidth = 1.5;
    for (let i = 0; i < 3; i++) { ctx.beginPath(); ctx.arc(W / 2, H * 0.4, (100 + i * 80) * p, 0, Math.PI * 2); ctx.stroke(); }
    ctx.font = `bold ${Math.round(52 * (0.5 + p * 0.5))}px Arial`; ctx.textAlign = 'center';
    ctx.globalAlpha = Math.min(p * 2, 1); ctx.fillStyle = '#FFF';
    ctx.fillText(branding.nom, W / 2, H * 0.42);
    ctx.fillRect(W / 2 - 40 * p, H * 0.46, 80 * p, 3);
    if (branding.afficherUrl) { ctx.font = 'bold 18px Arial'; ctx.fillStyle = 'rgba(255,255,255,0.7)'; ctx.fillText(branding.afficherUrl, W / 2, H * 0.52); }
    ctx.globalAlpha = 1;
    return;
  }

  // FOND + VIDEO
  ctx.fillStyle = '#0A0A1F'; ctx.fillRect(0, 0, W, H);
  if (video.readyState >= 2) {
    try {
      const vw = video.videoWidth || W; const vh = video.videoHeight || H;
      const kbP = (t - introEnd) / (duration - introEnd - 3);
      const sc = Math.max(W / vw, H / vh) * (1.0 + Math.max(0, kbP) * 0.06);
      ctx.drawImage(video, (W - vw * sc) / 2, (H - vh * sc) / 2, vw * sc, vh * sc);
    } catch {}
  }

  // OVERLAY
  const g = ctx.createLinearGradient(0, H * 0.5, 0, H);
  g.addColorStop(0, 'rgba(0,0,0,0)'); g.addColorStop(1, 'rgba(0,0,0,0.9)');
  ctx.fillStyle = g; ctx.fillRect(0, H * 0.5, W, H * 0.5);

  // BADGE
  if (branding && branding.position !== 'none') {
    const bx = branding.position === 'top-right' ? W - 214 : 22;
    const bw = Math.max(140, branding.nom.length * 13 + 60);
    ctx.fillStyle = (branding.couleur || '#6C47FF') + 'F2'; ctx.beginPath(); ctx.roundRect(bx, 22, bw, 48, 24); ctx.fill();
    ctx.fillStyle = '#FFF'; ctx.font = 'bold 19px Arial'; ctx.textAlign = 'center'; ctx.fillText(branding.nom + ' \u25CF', bx + bw / 2, 51);
  }

  // SOUS-TITRES ANIMÉS
  const sub = subs.find(s => t >= s.start && t < s.end);
  if (sub) {
    const sp = (t - sub.start) / (sub.end - sub.start);
    const fs = Math.round(H * 0.035);
    const slide = Math.max(0, (1 - Math.min(sp * 5, 1)) * 30);
    ctx.font = `bold ${fs}px Arial`; ctx.textAlign = 'center'; ctx.globalAlpha = Math.min(sp * 4, 1);
    const words = sub.text.split(' '); const lines: string[] = []; let cur = '';
    for (const w of words) { const test = cur ? cur + ' ' + w : w; if (ctx.measureText(test).width > W - 80) { if (cur) lines.push(cur); cur = w; } else cur = test; }
    if (cur) lines.push(cur);
    const lh = fs * 1.35; const baseY = H * 0.78 - (lines.length * lh) / 2 + slide;
    ctx.fillStyle = 'rgba(0,0,0,0.5)'; ctx.beginPath(); ctx.roundRect(W * 0.05, baseY - 5, W * 0.9, lines.length * lh + 20, 12); ctx.fill();
    lines.forEach((line, li) => { const y = baseY + li * lh + fs; ctx.strokeStyle = '#000'; ctx.lineWidth = 7; ctx.lineJoin = 'round'; ctx.strokeText(line, W / 2, y); ctx.fillStyle = '#FFF'; ctx.fillText(line, W / 2, y); });
    ctx.globalAlpha = 1;
  }

  // CTA ANIMÉ (dernières 3s)
  if (t >= outroStart && branding) {
    const op = (t - outroStart) / 3;
    ctx.fillStyle = `rgba(0,0,0,${op * 0.7})`; ctx.fillRect(0, 0, W, H);
    const pulse = 1 + Math.sin(op * Math.PI * 4) * 0.03;
    const bw = 320 * pulse; const bh = 56 * pulse;
    ctx.shadowColor = (branding.couleur || '#6C47FF') + '80'; ctx.shadowBlur = 20;
    ctx.fillStyle = branding.couleur || '#6C47FF'; ctx.beginPath(); ctx.roundRect(W / 2 - bw / 2, H * 0.55, bw, bh, bh / 2); ctx.fill();
    ctx.shadowBlur = 0; ctx.fillStyle = '#FFF'; ctx.font = 'bold 22px Arial'; ctx.textAlign = 'center';
    ctx.fillText('Essaie gratuitement \u2192', W / 2, H * 0.55 + bh / 2 + 8);
    ctx.font = 'bold 28px Arial'; ctx.fillText(branding.nom, W / 2, H * 0.68);
    if (branding.afficherUrl) { ctx.font = 'bold 16px Arial'; ctx.fillStyle = 'rgba(255,255,255,0.6)'; ctx.fillText(branding.afficherUrl, W / 2, H * 0.72); }
  }

  // URL discrète
  if (t < outroStart && branding?.afficherUrl) {
    ctx.font = `bold ${Math.round(H * 0.013)}px Arial`; ctx.textAlign = 'center';
    ctx.fillStyle = 'rgba(255,255,255,0.4)'; ctx.fillText(branding.afficherUrl, W / 2, H - 20);
  }
}

async function render(input: VideoInput): Promise<VideoOutput> {
  const { backgroundVideoUrl, audioB64, scriptForSubtitles, subtitles, branding, format = 'portrait', onProgress } = input;
  const log = (s: string, p: number) => { onProgress?.(s, p); console.log('[VideoEngine]', s); };
  const { w: W, h: H } = DIMS[format];

  // Vérification du navigateur avant tout
  const support = checkBrowserSupport();
  if (!support.supported) throw new VideoEngineError(support.reason);

  log('Décodage audio', 5);
  if (!audioB64 || audioB64.length < 100) {
    throw new VideoEngineError("L'audio est manquant. Générez d'abord la voix IA.");
  }
  if (!backgroundVideoUrl) {
    throw new VideoEngineError("Aucune vidéo de fond disponible.");
  }

  let audioBuffer: AudioBuffer;
  let audioCtx: AudioContext;
  try {
    const audioBytes = Uint8Array.from(atob(audioB64), c => c.charCodeAt(0));
    audioCtx = new AudioContext();
    audioBuffer = await audioCtx.decodeAudioData(audioBytes.buffer.slice(0));
  } catch (e) {
    console.error('[VideoEngine] Décodage audio échoué:', e);
    throw new VideoEngineError("Le fichier audio est corrompu. Regénérez la voix IA.");
  }

  const duration = audioBuffer.duration;
  if (!duration || duration < 0.5) {
    await audioCtx.close();
    throw new VideoEngineError("L'audio est trop court. Écrivez un script plus long.");
  }

  const subs = subtitles || generateSubtitles(scriptForSubtitles, duration);

  log('Chargement vidéo', 15);
  const canvas = document.createElement('canvas'); canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext('2d')!;
  const video = document.createElement('video');
  video.src = backgroundVideoUrl; video.muted = true; video.loop = true; video.playsInline = true; video.crossOrigin = 'anonymous';
  await new Promise<void>(r => { video.oncanplay = () => r(); video.onerror = () => r(); video.load(); setTimeout(r, 8000); });

  log('Setup audio', 20);
  const source = audioCtx.createBufferSource(); source.buffer = audioBuffer;
  const dest = audioCtx.createMediaStreamDestination();
  source.connect(dest); source.connect(audioCtx.destination);
  const canvasStream = canvas.captureStream(30);
  dest.stream.getAudioTracks().forEach(t => canvasStream.addTrack(t));
  const mimeType = ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm'].find(f => MediaRecorder.isTypeSupported(f)) || 'video/webm';
  const recorder = new MediaRecorder(canvasStream, { mimeType, videoBitsPerSecond: 2_500_000, audioBitsPerSecond: 128_000 });
  const chunks: BlobPart[] = [];
  recorder.ondataavailable = e => { if (e.data.size > 0) chunks.push(e.data); };

  return new Promise<VideoOutput>((resolve, reject) => {
    recorder.onstop = () => {
      const total = chunks.reduce((a, c) => a + (c as Blob).size, 0);
      canvasStream.getTracks().forEach(t => t.stop()); video.pause(); audioCtx.close();
      if (total < 5000) { reject(new VideoEngineError("La vidéo générée est vide. Réessayez ou changez de navigateur.")); return; }
      const blob = new Blob(chunks, { type: mimeType });
      log('Vidéo prête', 100);
      resolve({ blob, url: URL.createObjectURL(blob), mimeType, durationSec: duration, sizeBytes: blob.size });
    };
    recorder.onerror = (e: any) => { console.error('[VideoEngine] Recorder:', e); reject(new VideoEngineError("L'enregistrement vidéo a échoué. Réessayez.")); };
    log('Enregistrement', 25); recorder.start(500);
    video.play().catch(() => {}); Promise.resolve().then(() => source.start(0));
    const startTime = Date.now(); let rafId: number;
    const animate = () => {
      const elapsed = (Date.now() - startTime) / 1000;
      if (elapsed > duration + 0.5) return;
      drawFrame(ctx, video, elapsed, subs, W, H, branding, duration);
      log(`${Math.round(elapsed)}s / ${Math.round(duration)}s`, Math.min(25 + Math.round((elapsed / duration) * 70), 95));
      rafId = requestAnimationFrame(animate);
    };
    rafId = requestAnimationFrame(animate);
    source.onended = () => { cancelAnimationFrame(rafId); setTimeout(() => { recorder.requestData(); setTimeout(() => { if (recorder.state !== 'inactive') recorder.stop(); }, 400); }, 500); };
    setTimeout(() => { if (recorder.state === 'recording') { cancelAnimationFrame(rafId); recorder.requestData(); setTimeout(() => recorder.stop(), 300); } }, (duration + 10) * 1000);
  });
}

// MULTI-SCENE ENGINE
export interface MultiSceneParams { sceneUrls: string[]; sceneDurations: number[]; audioB64: string; script: string; produit?: string; onProgress?: (step: string, pct: number) => void; }

export async function mergeAllMultiScene(params: MultiSceneParams): Promise<Blob> {
  const { sceneUrls, audioB64, script, produit = 'ContenuIA', onProgress } = params;
  const log = (s: string, p: number) => { onProgress?.(s, p); };
  const W = 720; const H = 1280;

  const support = checkBrowserSupport();
  if (!support.supported) throw new VideoEngineError(support.reason);

  log('Décodage audio', 5);
  if (!audioB64 || audioB64.length < 100) {
    throw new VideoEngineError("L'audio est manquant. Générez d'abord la voix IA.");
  }
  if (!sceneUrls?.length) {
    throw new VideoEngineError("Aucune vidéo disponible pour le montage.");
  }

  let buf: AudioBuffer;
  let ac: AudioContext;
  try {
    const ab = Uint8Array.from(atob(audioB64), c => c.charCodeAt(0));
    ac = new AudioContext();
    buf = await ac.decodeAudioData(ab.buffer.slice(0));
  } catch (e) {
    console.error('[VideoEngine] Décodage audio échoué:', e);
    throw new VideoEngineError("Le fichier audio est corrompu. Regénérez la voix IA.");
  }

  const dur = buf.duration;
  if (!dur || dur < 0.5) {
    await ac.close();
    throw new VideoEngineError("L'audio est trop court. Écrivez un script plus long.");
  }
  const subs = generateSubtitles(script, dur);
  log('Chargement vidéos', 15);
  const videos = await Promise.all(sceneUrls.slice(0, 5).map(url => new Promise<HTMLVideoElement>(r => {
    const v = document.createElement('video'); v.src = url; v.muted = true; v.loop = true; v.playsInline = true; v.crossOrigin = 'anonymous';
    v.oncanplay = () => r(v); v.onerror = () => r(v); v.load(); setTimeout(() => r(v), 8000);
  })));
  const canvas = document.createElement('canvas'); canvas.width = W; canvas.height = H; const ctx = canvas.getContext('2d')!;
  const src = ac.createBufferSource(); src.buffer = buf; const dest = ac.createMediaStreamDestination();
  src.connect(dest); src.connect(ac.destination);
  const cs = canvas.captureStream(30); dest.stream.getAudioTracks().forEach(t => cs.addTrack(t));
  const mt = ['video/webm;codecs=vp9,opus', 'video/webm'].find(f => MediaRecorder.isTypeSupported(f)) || 'video/webm';
  const rec = new MediaRecorder(cs, { mimeType: mt, videoBitsPerSecond: 3_000_000, audioBitsPerSecond: 128_000 });
  const chunks: BlobPart[] = []; rec.ondataavailable = e => { if (e.data.size > 0) chunks.push(e.data); };
  return new Promise<Blob>((resolve, reject) => {
    rec.onstop = () => { const t = chunks.reduce((a, c) => a + (c as Blob).size, 0); cs.getTracks().forEach(x => x.stop()); videos.forEach(v => v.pause()); ac.close(); if (t < 5000) { reject(new VideoEngineError("La vidéo générée est vide. Réessayez ou changez de navigateur.")); return; } resolve(new Blob(chunks, { type: mt })); };
    rec.onerror = (e: any) => { console.error('[VideoEngine] MultiScene recorder:', e); reject(new VideoEngineError("L'enregistrement vidéo a échoué. Réessayez.")); };
    rec.start(500); videos.forEach(v => v.play().catch(() => {})); src.start(0);
    const nS = Math.min(videos.length, 5); const dps = dur / nS; const st = Date.now(); let raf: number;
    const KB = [{ ss: 1, es: 1.08, px: 0 }, { ss: 1.08, es: 1, px: -0.02 }, { ss: 1, es: 1.06, px: 0.02 }, { ss: 1.05, es: 1, px: 0 }, { ss: 1, es: 1.1, px: 0 }];
    const anim = () => {
      const el = (Date.now() - st) / 1000; if (el > dur + 0.5) return;
      const si = Math.min(Math.floor(el / dps), nS - 1); const sp = (el - si * dps) / dps;
      const v = videos[si]; const kb = KB[si % KB.length];
      ctx.fillStyle = '#0A0A1F'; ctx.fillRect(0, 0, W, H);
      if (v && v.readyState >= 2) { try { const vw = v.videoWidth||W; const vh = v.videoHeight||H; const s = Math.max(W/vw, H/vh) * (kb.ss + (kb.es-kb.ss)*sp); ctx.drawImage(v, (W-vw*s)/2 + kb.px*sp*W, (H-vh*s)/2, vw*s, vh*s); } catch {} }
      if (sp < 0.08) { ctx.fillStyle = `rgba(0,0,0,${1-sp/0.08})`; ctx.fillRect(0,0,W,H); }
      const g2 = ctx.createLinearGradient(0, H*0.55, 0, H); g2.addColorStop(0,'rgba(0,0,0,0)'); g2.addColorStop(1,'rgba(0,0,0,0.88)'); ctx.fillStyle = g2; ctx.fillRect(0, H*0.55, W, H*0.45);
      const bw2 = Math.max(140, produit.length*12+60); ctx.fillStyle = 'rgba(108,71,255,0.92)'; ctx.beginPath(); ctx.roundRect(20,20,bw2,46,23); ctx.fill();
      ctx.fillStyle='#FFF'; ctx.font='bold 18px Arial'; ctx.textAlign='center'; ctx.fillText(produit+' \u25CF', 20+bw2/2, 48);
      for (let i=0;i<nS;i++){ctx.fillStyle=i===si?'rgba(255,255,255,0.9)':'rgba(255,255,255,0.25)';ctx.beginPath();ctx.arc(W-20-(nS-1-i)*14,34,i===si?5:3,0,Math.PI*2);ctx.fill();}
      const sub=subs.find(s=>el>=s.start&&el<s.end);
      if(sub){const fs2=Math.round(H*0.035);ctx.font=`bold ${fs2}px Arial`;ctx.textAlign='center';const w2=sub.text.split(' ');const ls:string[]=[];let c2='';for(const w of w2){const t2=c2?c2+' '+w:w;if(ctx.measureText(t2).width>W-80){if(c2)ls.push(c2);c2=w;}else c2=t2;}if(c2)ls.push(c2);const lh2=fs2*1.35;const by2=H*0.78-(ls.length*lh2)/2;ctx.fillStyle='rgba(0,0,0,0.5)';ctx.beginPath();ctx.roundRect(W*0.05,by2-5,W*0.9,ls.length*lh2+20,12);ctx.fill();ls.forEach((l,li)=>{const y=by2+li*lh2+fs2;ctx.strokeStyle='#000';ctx.lineWidth=7;ctx.lineJoin='round';ctx.strokeText(l,W/2,y);ctx.fillStyle='#FFF';ctx.fillText(l,W/2,y);});}
      if(el>=dur-3){const op2=(el-(dur-3))/3;ctx.fillStyle=`rgba(0,0,0,${op2*0.7})`;ctx.fillRect(0,0,W,H);ctx.fillStyle='#6C47FF';ctx.beginPath();ctx.roundRect(W/2-160,H*0.55,320,56,28);ctx.fill();ctx.fillStyle='#FFF';ctx.font='bold 22px Arial';ctx.fillText('Essaie gratuitement \u2192',W/2,H*0.55+36);ctx.font='bold 28px Arial';ctx.fillText(produit,W/2,H*0.68);}
      log(`Scène ${si+1}/${nS}`, Math.min(30+Math.round((el/dur)*65), 95));
      raf = requestAnimationFrame(anim);
    };
    raf = requestAnimationFrame(anim);
    src.onended = () => { cancelAnimationFrame(raf); setTimeout(() => { rec.requestData(); setTimeout(() => { if (rec.state !== 'inactive') rec.stop(); }, 400); }, 500); };
    setTimeout(() => { if (rec.state === 'recording') { cancelAnimationFrame(raf); rec.requestData(); setTimeout(() => rec.stop(), 300); } }, (dur + 12) * 1000);
  });
}

export const VideoEngine = { VERSION: '1.0.0', render, generateSubtitles, checkBrowserSupport, FORMATS: ['portrait', 'landscape', 'square'] as const };
export default VideoEngine;
